// src/pages/Sugerencias.tsx — TELLGM-01 v1.1 (Oct 29, 2026, owner: "Sugerencias needs to be English"): the Admin page and tab read "Suggestions";
// the Maestro's words still show in Spanish AND English. v1: Admin › Suggestions: everything Maestros send with the "🎤 Dile al equipo GM" button.
// First kind: catalog suggestions from the quote tool. Each message shows his words in the language he used, plus Spanish AND English
// (owner: "with the spanish translation when it arrives in the Ops console"), his voice note, and what the screen knew (what, kind of work,
// how he charges it, his usual price, how often, his search words, the closest catalog items).
// Reads and writes go through gm-feedback (ops actions check ops_role(): pricing_admin / owner).
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

interface Item {
  id: number; kind: string; status: string; maestro: string; created_at: string; lang: string; said_lang: string | null;
  note: string | null; voice_url: string | null; text_es: string | null; text_en: string | null; processed_at: string | null; process_error: string | null;
  context: Record<string, unknown>; closest: { id: string; name: string; name_es: string | null }[]; ops_note: string | null; reviewed_by: string | null;
}
const KIND: Record<string, string> = { catalog_suggestion: "Catalog suggestion", general: "Message" };
const UNIT: Record<string, string> = { job: "per job", ft: "per foot", sqft: "per sq ft", each: "each", hour: "per hour", yard: "per cubic yard" };
const OFTEN: Record<string, string> = { rare: "now and then", monthly: "every month", weekly: "every week" };
const S = {
  card: { background: "#fff", border: "1px solid #ddd", borderRadius: 12, padding: 14, marginBottom: 14 } as const,
  btn: { minHeight: 40, padding: "0 14px", borderRadius: 8, border: "1px solid #1F3D2B", background: "#fff", color: "#1F3D2B", fontWeight: 600, cursor: "pointer" } as const,
  muted: { color: "#666", fontSize: 13 } as const,
  lang: { flex: "1 1 260px", background: "#F6F6F2", borderRadius: 8, padding: 10, fontSize: 15, whiteSpace: "pre-wrap" as const },
};
async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("gm-feedback", { body });
  if (error) {
    let b: { message_en?: string; error?: string } | null = null;
    try { b = await (error as { context?: Response }).context?.json?.() ?? null; } catch { /* generic */ }
    throw new Error(b?.message_en ?? b?.error ?? "Something went wrong.");
  }
  return data as T;
}
const when = (iso: string) => new Date(iso).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function Sugerencias() {
  const [items, setItems] = useState<Item[] | null>(null);
  const [all, setAll] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const load = useCallback(async () => {
    try { setItems((await call<{ items: Item[] }>({ action: "ops_list", status: all ? "all" : "open" })).items); setErr(null); }
    catch (e) { setErr((e as Error).message); }
  }, [all]);
  useEffect(() => { load(); }, [load]);
  async function set(id: number, status: string) {
    setBusy(id);
    try { await call({ action: "ops_update", id, status, ops_note: notes[id] ?? null }); await load(); }
    catch (e) { setErr(`#${id}: ${(e as Error).message}`); }
    setBusy(null);
  }
  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 16 }}>
      <h2 style={{ marginBottom: 4 }}>Suggestions</h2>
      <p style={S.muted}>What Maestros send with the “🎤 Tell the GM team” button (“Dile al equipo GM” in the app). Every message shows in English and Spanish.</p>
      <label style={{ display: "inline-flex", gap: 8, alignItems: "center", margin: "6px 0 12px" }}>
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show done / added / declined too
      </label>
      {err && <p style={{ color: "#C0392B" }} role="alert">{err}</p>}
      {!items ? <p className="loading">Loading…</p> : items.length === 0 ? <p>Nothing new. 🎉</p> : items.map((x) => {
        const c = x.context ?? {};
        return (
          <div key={x.id} style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <b style={{ fontSize: 16 }}>#{x.id} · {KIND[x.kind] ?? x.kind} · {x.maestro}</b>
              <span style={{ ...S.muted, fontWeight: 700 }}>{x.status.toUpperCase()} · {when(x.created_at)}</span>
            </div>
            {x.kind === "catalog_suggestion" && (
              <div style={{ margin: "8px 0", fontSize: 14, lineHeight: 1.6 }}>
                <div><b>What:</b> {String(c.what || "—")}</div>
                <div><b>Kind of work:</b> {String(c.trade || "—")} · <b>Charged:</b> {UNIT[String(c.unit)] ?? "—"} · <b>His usual price:</b> {c.usual_price_dollars ? `$${c.usual_price_dollars}` : "—"} · <b>How often:</b> {OFTEN[String(c.often)] ?? "—"}</div>
                <div style={S.muted}>Searched “{String(c.search || "")}”{c.zip ? ` · ZIP ${String(c.zip)}` : ""}{x.closest.length ? ` · closest in catalog: ${x.closest.map((k) => `${k.name} (${k.id})`).join(", ")}` : " · nothing close in the catalog"}</div>
              </div>
            )}
            {x.voice_url && <audio src={x.voice_url} controls style={{ width: "100%", margin: "4px 0" }} />}
            {x.processed_at ? (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
                <div style={S.lang}><div style={{ ...S.muted, fontWeight: 700, marginBottom: 4 }}>ENGLISH{x.said_lang === "en" ? " (his words)" : " (translation)"}</div>{x.text_en}</div>
                <div style={S.lang}><div style={{ ...S.muted, fontWeight: 700, marginBottom: 4 }}>SPANISH{x.said_lang === "es" ? " (his words)" : " (translation)"}</div>{x.text_es}</div>
              </div>
            ) : (
              <p style={S.muted}>{x.note ? `Typed: “${x.note}”. ` : ""}Translation pending{x.process_error ? ` (retrying: ${x.process_error.slice(0, 80)})` : ""}.</p>
            )}
            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
              <input placeholder="Ops note (e.g. added as fba-012, or why not)" value={notes[x.id] ?? x.ops_note ?? ""}
                onChange={(e) => setNotes((n) => ({ ...n, [x.id]: e.target.value }))} style={{ minHeight: 40, padding: "0 10px", borderRadius: 8, border: "1px solid #ccc" }} />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {x.kind === "catalog_suggestion"
                  ? [["reviewing", "Looking at it"], ["added", "Added to catalog"], ["declined", "Not now"]].map(([s, l]) =>
                      <button key={s} type="button" style={{ ...S.btn, ...(x.status === s ? { background: "#1F3D2B", color: "#fff" } : {}) }} disabled={busy === x.id} onClick={() => set(x.id, s)}>{l}</button>)
                  : [["reviewing", "Looking at it"], ["done", "Done"]].map(([s, l]) =>
                      <button key={s} type="button" style={{ ...S.btn, ...(x.status === s ? { background: "#1F3D2B", color: "#fff" } : {}) }} disabled={busy === x.id} onClick={() => set(x.id, s)}>{l}</button>)}
                {x.reviewed_by && <span style={S.muted}>by {x.reviewed_by}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
